import { FieldType, UIPermission, Column, FieldValue, O, PanelWrapper, Filters, FilterField, option, Option, Action, ModalId, useUIActions, useLocalStorageActions } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { useSystemStateHooks } from "../../../../system"
import { Budget } from "../../../state/types"
import { Data2, DataTypeList2 } from "../../../../../features/data-types"
import { useCustomerLoaders } from "../../../loaders/customerLoaders"
import { Params } from "../../../../../state/reducers/types"
import { useEffect, useMemo, useState } from "react"
import { SellerField } from "../../seller-field/sellerField"
import { useEmployeeLoaders, useEmployeeHooks } from "../../../../administration"
import { useLocation, useNavigate } from "react-router-dom"
import { RoutePath } from "../../../../../features/routes"
import { BookmarkButton } from "../../../../../features/bookmarks"
import { startOfPreviousMonth, toDate } from "../../../../../utils/typeUtils"
import { useCustomerHooks } from "../../../hooks/customerHooks"
import { useBudgetHooks } from "../../../hooks/budgetHooks"
import { useBudgetLoaders } from "../../../loaders/budgetLoaders"
import { DownloadBudgetPDFModal } from "../download-budget-pdf-modal/downloadBudgetPDFModal"

export const BudgetList = () => {
    const filtersKey = 'budgets'
    const { getFiltersData } = useLocalStorageActions()
    const filtersData = useMemo(() => getFiltersData()[filtersKey], [])

    const [searchText, setSearchText] = useState<O<string>>()
    const [sellerId, setSellerId] = useState<O<string>>(filtersData?.sellerId)
    const [customerOptions, setCustomerOptions] = useState<Option[]>([])
    const [customerId, setCustomerId] = useState<O<string>>(filtersData?.customerId)
    // Por defecto solo el mes en curso y el anterior (si el usuario no tiene otra fecha guardada)
    const [dateFrom, setDateFrom] = useState<O<Date>>(filtersData?.dateFrom ? toDate(filtersData.dateFrom) : startOfPreviousMonth())
    const [isDefaultDateFrom, setIsDefaultDateFrom] = useState<boolean>(!filtersData?.dateFrom)
    const [dateTo, setDateTo] = useState<O<Date>>(toDate(filtersData?.dateTo))
    const [currentBudget, setCurrentBudget] = useState<Budget>()

    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_BUDGETS)
    const { loadFilteredBatch, countFiltered } = useBudgetHooks()
    const { describeCustomer } = useCustomerHooks()
    const { describeEmployee } = useEmployeeHooks()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const budgetLoaders = useBudgetLoaders()
    const customerLoaders = useCustomerLoaders()
    const employeeLoaders = useEmployeeLoaders()

    const { openModal } = useUIActions()

    const loadCustomerOptions = async () => {
        const { loadAllCustomers } = customerLoaders()
        const customers = await loadAllCustomers(true)
        return customers.map(customer =>
            option(customer.id, describeCustomer(customer))
        )
    }

    useEffect(() => {
        loadCustomerOptions().then(setCustomerOptions)
    }, [])

    // Al buscar por texto se ignora la fecha por defecto, para encontrar presupuestos viejos
    const queryDateFrom = isDefaultDateFrom && searchText?.trim() ? undefined : dateFrom

    const filtersText = useMemo(() =>
        [searchText, sellerId, customerId, queryDateFrom?.toISOString(), dateTo?.toISOString()].filter(Boolean).join(';')
    , [searchText, sellerId, customerId, queryDateFrom, dateTo])

    const onSearch = (value?: FieldValue) => setSearchText(value as O<string>)

    const searchField: FilterField = {
        name: 'search',
        type: FieldType.TEXT,
        placeholder: 'Buscar por código, vendedor o cliente',
        icon: 'search',
        onChange: onSearch
    }

    const onChangeSeller = (value?: FieldValue) => setSellerId(value as O<string>)

    const onChangeCustomer = (value?: FieldValue) => setCustomerId(value as O<string>)

    const onChangeDateFrom = (value?: FieldValue) => {
        const newDateFrom = value as O<Date>
        newDateFrom?.setHours(0, 0, 0, 0)
        setDateFrom(newDateFrom)
        setIsDefaultDateFrom(false)
    }
    
    const onChangeDateTo = (value?: FieldValue) => {
        const newDateTo = value as O<Date>
        newDateTo?.setHours(23, 59, 59, 999)
        setDateTo(newDateTo)
    }

    const filterFields: FilterField[] = [
        {
            ...SellerField({
                defaultValue: sellerId,
                active: true,
                onChange: onChangeSeller
            }),
            space: 3
        } as FilterField,
        {
            name: 'customerId',
            type: FieldType.SELECT,
            label: 'Cliente',
            defaultValue: customerId,
            options: customerOptions,
            loading: customerOptions.length === 0,
            space: 3,
            onChange: onChangeCustomer
        },
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Fecha: Desde',
            defaultValue: dateFrom,
            space: 2,
            onChange: onChangeDateFrom
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Fecha: Hasta',
            defaultValue: dateTo,
            space: 2,
            onChange: onChangeDateTo
        }
    ]

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'date',
            type: FieldType.DATE,
            label: 'Fecha'
        },
        {
            name: 'sellerId',
            type: FieldType.TABLE,
            table: TableName.EMPLOYEES,
            label: 'Vendedor',
            render: (value?: FieldValue) => {
                const seller = employeeLoaders().findEmployee(value! as string)!
                return describeEmployee(seller)
            }
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente'
        }
    ]

    const loadBatch = async (startKey?: Params, dbRequest = false) => {
        return loadFilteredBatch(searchText, sellerId, customerId, queryDateFrom, dateTo, startKey, 10, dbRequest)
    }

    const count = async (startKey?: Params) => {
        return countFiltered(searchText, sellerId, customerId, queryDateFrom, dateTo, startKey)
    }

    const toRow = (budgetDT: DataType): Data2 => {
        const { items, ...data } = budgetDT as Budget
        return data
    }

    const onDownloadPDF = (budget: Budget) => () => {
        setCurrentBudget(budget)
        openModal(ModalId.DOWNLOAD_BUDGET_PDF)
    }

    const onCreateOperation = (id?: string) => {
        navigate(id!)
        setTimeout(() => openModal(ModalId.CREATE_OPERATION_FROM_BUDGET), 500)
    }

    const onRemove = (budget: Budget) => () => {
        setCurrentBudget(budget)
        openModal(ModalId.REMOVE)
    }

    const actions = async (rowIdValue: string) => {
        const { loadBudget } = budgetLoaders()
        const budget = await loadBudget(rowIdValue)

        const budgetHref = `${pathname}/${rowIdValue}`
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: budgetHref },
            { icon: 'download', kind: 'tertiary', tooltip: 'Descargar', onClick: onDownloadPDF(budget!) }
        ]

        if (!readOnly) {
            actionList.push(
                { icon: 'arrow-repeat', tooltip: 'Actualizar', href: `${budgetHref}/${RoutePath.UPDATE}` },
                { icon: 'tags-fill', tooltip: 'Crear Venta/Acopio', onClick: onCreateOperation },
                { icon: 'trash-fill', kind: 'secondary', tooltip: 'Eliminar', onClick: onRemove(budget!) }
            )
        }

        return actionList
    }

    return (
        <PanelWrapper
            title="Presupuestos"
            wide
            titleRight={<BookmarkButton routePath={RoutePath.BUDGETS} />}
        >
            <Filters
                filtersKey={filtersKey}
                searchField={searchField}
                fields={filterFields}
            />
            <DataTypeList2
                columns={columns}
                filtersText={filtersText}
                currentDataTypeParam={currentBudget}
                readOnly={readOnly}
                toRow={toRow}
                loadBatch={loadBatch}
                count={count}
                actions={actions}
            />
            <DownloadBudgetPDFModal budget={currentBudget} />
        </PanelWrapper>
    )
}
